import React, { useState, useEffect, useRef } from 'react';
//import profileImage from './assets/profile.jpeg';
import resumePdf from './assets/resume.pdf';
import './App.css';

function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [isLoading, setIsLoading] = useState(true);
  
  // Refs for each section
  const homeRef = useRef(null);
  const aboutRef = useRef(null);
  const readingRef = useRef(null);
  const experienceRef = useRef(null);
  const contactRef = useRef(null);
  
  useEffect(() => {
    // Simulate content loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1200);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Handle navigation clicks
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    
    const sectionRef = {
      'home': homeRef,
      'about': aboutRef,
      'experience': experienceRef,
      'contact': contactRef,
      'reading': readingRef
    }[sectionId];
    
    if (sectionRef && sectionRef.current) {
      sectionRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  
  // Handle scroll position to update active nav item
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100; // Offset for header
      
      const sections = [
        { id: 'home', ref: homeRef },
        { id: 'about', ref: aboutRef },
        { id: 'experience', ref: experienceRef },
        { id: 'contact', ref: contactRef },
        { id: 'reading', ref: readingRef }
      ];
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.ref.current && section.ref.current.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Reading data - easy to update
  const readingData = {
    current: [
      {
        title: "AWQ: Activation-aware Weight Quantization for On-Device LLM Compression and Acceleration",
        authors: "Lin et al.",
        year: "2023",
        link: "https://arxiv.org/html/2306.00978v5",
        note: "Exploring efficient quantization techniques for deploying large models on edge devices"
      }
    ],
    recent: [
      {
        title: "Attention Is All You Need",
        authors: "Vaswani et al.",
        year: "2017",
        link: "https://arxiv.org/abs/1706.03762",
        note: "Revisiting the transformer architecture fundamentals"
      },
      {
        title: "PaLM: Scaling Language Modeling with Pathways",
        authors: "Chowdhery et al.",
        year: "2022",
        link: "https://arxiv.org/abs/2204.02311",
        note: "Exploring distributed training techniques"
      },
      {
        title: "ZeRO: Memory Optimizations Toward Training Trillion Parameter Models",
        authors: "Rajbhandari et al.",
        year: "2020",
        link: "https://arxiv.org/abs/1910.02054",
        note: "Memory optimization strategies for large-scale training"
      }
    ]
  };

  // Timeline data for experience section
  const experienceData = [
    {
      period: "Fall 2024 - Present",
      company: "Amazon",
      role: "AWS Aurora Storage Node",
      description: "Working on customer-facing features and projects to improve performance, durability, and reliability of the Aurora database service. Main areas of development are around performance and resource optimization. Work is in C++.",
      technologies: ["Distributed Systems", "C++", "Backend Development", "Asyncronous Programming"]
    },
    {
      period: "Fall 2023 - Summer 2024",
      company: "Startup",
      role: "Co-founder / Technical Lead",
      description: "Led technical development for a fintech startup aimed at democratizing access to financial advice:",
      achievements: [
        "Advanced to investment round of Dempsey Startup Competition",
        "Accepted into DubHacks Next Batch 3",
        "Designed and built web scraping infrastructure on AWS",
        "Developed data vectorization pipeline for financial articles",
        "Created user profile-based recommendation system for personalized financial insights",
        "Designed and developed iOS application with Plaid integration for financial account tracking",
        "Implemented real-time chat for financial inquiries and financial roadmap features"
      ],
      technologies: ["AWS", "Python", "Scrapy", "Dart", "Vector Databases", "Plaid API"]
    },
    {
      period: "Summer 2023", company: "Amazon",
      role: "Intern - Aurora Storage Node",
      description: "Enhanced a micro-benchmarking tool designed to allow engineers to quickly benchmark the performance of changes. Main areas of contribution were expanding the feature set and working within the asynchronous, multi-threaded load generation framework.",
      technologies: ["Distributed Systems", "C++", "Backend Development"]
    },
    {
      period: "Summer 2022",
      company: "Amazon",
      role: "Intern - AWS EC2 Networking",
      description: "Worked on the VPC (Virtual Private Cloud) Border Gateways team, network hop optimizations by introducing a new tunneling protocol.",
      technologies: ["AWS", "Networking", "Cloud Infrastructure", "VPC"]
    }
  ];

  return (
    <div className="App">
      {isLoading ? (
        <div className="loading-screen">
          <div className="loading-animation">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
        </div>
      ) : (
        <>
          <header className="App-header">
            <div className="header-content">
              <nav className="App-nav">
                <ul>
                  <li className={activeSection === 'about' ? 'active' : ''}>
                    <button onClick={() => scrollToSection('about')}>About</button>
                  </li>
                  <li className={activeSection === 'experience' ? 'active' : ''}>
                    <button onClick={() => scrollToSection('experience')}>Experience</button>
                  </li>
                  <li className={activeSection === 'contact' ? 'active' : ''}>
                    <button onClick={() => scrollToSection('contact')}>Contact</button>
                  </li>
                  {/* <li className={activeSection === 'reading' ? 'active' : ''}>
                    <button onClick={() => scrollToSection('reading')}>Reading</button>
                  </li> */}
                </ul>
              </nav>
            </div>
          </header>
          
          <main className="App-main">
            {/* Profile Section */}
            <section id="home" ref={homeRef} className="profile-section">
              <div className="container">
                <img 
	          src="/assets/profile.jpeg"
                  alt="Daniel Tameishi" 
                  className="profile-photo" 
                />
                <h1 className="profile-name">Daniel Tameishi</h1>
                <p className="profile-title">Software Engineer at AWS (Aurora Storage) </p>
                <div className="profile-location">
                  <span role="img" aria-label="Location">📍</span> Seattle, WA
                </div>
                <div className="social-links">
                  <a href="https://github.com/tameidan" className="social-link github" target="_blank" rel="noopener noreferrer" title="GitHub">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
                    </svg>
                  </a>
                  <a href="https://www.linkedin.com/in/danieltameishi/" className="social-link linkedin" target="_blank" rel="noopener noreferrer" title="LinkedIn">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                  <a href="mailto:danieltameishi@gmail.com" className="social-link email" title="Email">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                      <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-.904.732-1.636 1.636-1.636h3.819l6.545 4.91 6.545-4.91h3.819A1.636 1.636 0 0 1 24 5.457z"/>
                    </svg>
                  </a>
                  <a href={resumePdf} className="social-link resume" target="_blank" rel="noopener noreferrer" title="Resume">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </section>
            
            {/* About Section - Reorganized */}
            <section id="about" ref={aboutRef} className="section">
              <div className="container">
                <h2 className="section-title">About</h2>
                
                {/* Main intro paragraph */}
                <div className="about-intro">
                <p>
                Currently a Software Engineer at Amazon working on AWS Aurora's Storage Node team, where I build and maintain the distributed storage infrastructure that
                powers Aurora's cloud-native database service. Aurora is AWS's cloud-native
                relational database that separates compute from storage to deliver MySQL and PostgreSQL compatibility with enterprise-grade performance,
                availability, and automatic scaling. The team I am on focus on the log store portion of the service where customer data is stored. Most of my current work
                is related to a new, customer-facing feature.<br></br> <br></br>

                Previously co-founded a fintech startup with two of my closest friends from college. Primary was was to democratize access to financial advice through an iOS application.

                I'm passionate about distributed systems and systems programming, and in my free time,
                I explore content related to distributed training and inference for machine learning models. Outside of work, I enjoy playing tennis, basketball, and going to Husky games!
                </p>
                </div>

                {/* Education and Skills in balanced columns */}
                <div className="about-grid">
                  <div className="about-left">
                    <div className="education-item">
                      <h4>University of Washington</h4>
                      <p className="degree">Bachelor of Science in Computer Science</p>
                      <p className="gpa">GPA: 3.94 (cum laude)</p>
                    </div>
                    
                    <div className="focus-areas">
                      <h4>Focus Areas</h4>
                      <p>Distributed Systems • Computer Networking • AI Applications • Systems Programming</p>
                    </div>
                  </div>
                  
                  <div className="about-right">
                    <div className="skills-section">
                      <h4>Technical Skills</h4>
                      <div className="skill-tags">
                        <span className="skill-tag">C++</span>
                        <span className="skill-tag">C</span>
                        <span className="skill-tag">Java</span>
                        <span className="skill-tag">Distributed Systems</span>
                        <span className="skill-tag">Computer Networking</span>
                        <span className="skill-tag">Systems Programming</span>
                      </div>
                    </div>
                    
                    <div className="interests-section">
                      <h4>Research Interests</h4>
                      <div className="skill-tags">
                        <span className="skill-tag">Large Language Models</span>
                        <span className="skill-tag">Model Distillation</span>
                        <span className="skill-tag">Distributed Training</span>
                        <span className="skill-tag">Distributed Inference</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            
            {/* Experience Section */}
            <section id="experience" ref={experienceRef} className="section">
              <div className="container">
                <h2 className="section-title">Experience</h2>
                <div className="experience-timeline">
                  <div className="timeline-track"></div>
                  {experienceData.map((experience, index) => (
                    <div className="timeline-item" key={index}>
                      <div className="timeline-marker"></div>
                      <div className="timeline-content">
                        <div className="timeline-period">{experience.period}</div>
                        <h3 className="timeline-title">{experience.role}</h3>
                        <h4 className="timeline-company">{experience.company}</h4>
                        <p className="timeline-description">{experience.description}</p>
                        {experience.achievements && (
                          <ul className="timeline-achievements">
                            {experience.achievements.map((achievement, i) => (
                              <li key={i}>{achievement}</li>
                            ))}
                          </ul>
                        )}
                        <div className="timeline-technologies">
                          {experience.technologies.map((tech, i) => (
                            <span className="tech-tag" key={i}>{tech}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
            
            {/* Contact Section */}
            <section id="contact" ref={contactRef} className="section">
              <div className="container">
                <h2 className="section-title">Contact</h2>
                <div className="contact-grid">
                  <div className="contact-info">
                    <p>Always happy to chat, feel free to reach out:</p>
                    <div className="contact-links">
                      <a href="mailto:danieltameishi@gmail.com" className="contact-link">
                        <span className="contact-icon">✉️</span>
                        <span>danieltameishi@gmail.com</span>
                      </a>
                      <a href="https://www.linkedin.com/in/danieltameishi/" className="contact-link" target="_blank" rel="noopener noreferrer">
                        <span className="contact-icon">🔗</span>
                        <span>linkedin.com/in/danieltameishi</span>
                      </a>
                      <a href="https://github.com/tameidan" className="contact-link" target="_blank" rel="noopener noreferrer">
                        <span className="contact-icon">💻</span>
                        <span>github.com/tameidan</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            
            {/* Reading Section - COMMENTED OUT */}
            {/* <section id="reading" ref={readingRef} className="section">
              <div className="container">
                <h2 className="section-title">Currently Reading</h2>
                
                <div className="reading-category">
                  <h3>Current</h3>
                  <div className="reading-list">
                    {readingData.current.map((item, index) => (
                      <div className="reading-item" key={index}>
                        <div className="reading-content">
                          <h4 className="reading-title">
                            <a href={item.link} target="_blank" rel="noopener noreferrer">
                              {item.title}
                            </a>
                          </h4>
                          <p className="reading-authors">{item.authors} ({item.year})</p>
                          <p className="reading-note">{item.note}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="reading-category">
                  <h3>Recently Finished</h3>
                  <div className="reading-list">
                    {readingData.recent.map((item, index) => (
                      <div className="reading-item recent" key={index}>
                        <div className="reading-content">
                          <h4 className="reading-title">
                            <a href={item.link} target="_blank" rel="noopener noreferrer">
                              {item.title}
                            </a>
                          </h4>
                          <p className="reading-authors">{item.authors} ({item.year})</p>
                          <p className="reading-note">{item.note}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section> */}
          </main>
          
          <footer className="App-footer">
            <div className="container">
              <div className="footer-content">
                <p>© {new Date().getFullYear()} Daniel Tameishi</p>
                <div className="footer-links">
                  <a href="https://github.com/tameidan" target="_blank" rel="noopener noreferrer">GitHub</a>
                  <a href="https://www.linkedin.com/in/danieltameishi/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
                </div>
              </div>
            </div>
          </footer>
        </>
      )}
    </div>
  );
}

export default App;
